import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'react-toastify';
import { useAddPatient, useConsentTemplates } from '../hooks/useApi';



import { useAuth } from '../context/AuthContext';

import { UserPlus, FileText, PenTool, Trash2 } from 'lucide-react';


const AddPatient = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const signaturePadRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: '',
    contactNumber: '',
    age: '',
    gender: '',
    address: '',
    diagnosis: [],
    diagnosisInput: '',
    treatmentPlan: '',
    medicalHistory: '',
  });

  const [consentStep, setConsentStep] = useState(0);


  const [consentData, setConsentData] = useState({
    templateType: '',
    medicalPhotographyConsent: '',
    researchAcademicConsent: '',
    treatmentDocumentationConsent: '',
    signature: null,
  });

  const [currentStep, setCurrentStep] = useState(1);

  // Fetch consent templates from API
  const { data: consentTemplatesData, isLoading: isLoadingTemplates } = useConsentTemplates();

  // Extract templates from API response: {status, message, data: [{id, name, description}, ...]}
  const consentTemplates = consentTemplatesData?.data || [];

  // Set default template when templates are loaded
  useEffect(() => {
    if (consentTemplates.length > 0 && !consentData.templateType) {
      setConsentData((prev) => ({ ...prev, templateType: String(consentTemplates[0].id) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentTemplates.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    signaturePadRef.current?.clear();
  };

  const validatePatientInfo = () => {
    const { fullName, contactNumber, age, gender, diagnosis, treatmentPlan } = formData;

    if (!fullName || !contactNumber || !age || !gender || !diagnosis || !treatmentPlan) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateConsent = () => {
    if (!consentData.medicalPhotographyConsent || !consentData.researchAcademicConsent || !consentData.treatmentDocumentationConsent) {
      toast.error('Please provide consent for all three consent types');
      return false;
    }
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Please provide a signature to continue');
      return false;
    }
    return true;
  };

  const handleDiagnosisKeyDown = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();

      const value = formData.diagnosisInput.trim();

      if (!value) return;

      // avoid duplicates
      if (formData.diagnosis.includes(value)) {
        setFormData((prev) => ({ ...prev, diagnosisInput: '' }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        diagnosis: [...prev.diagnosis, value],
        diagnosisInput: '',
      }));
    }
  };

  const removeDiagnosis = (index) => {
    setFormData((prev) => ({
      ...prev,
      diagnosis: prev.diagnosis.filter((_, i) => i !== index),
    }));
  };

  const commitDiagnosisInput = () => {
  const value = formData.diagnosisInput.trim();

  if (!value) return;

  // avoid duplicates
  if (formData.diagnosis.includes(value)) {
    setFormData((prev) => ({ ...prev, diagnosisInput: '' }));
    return;
  }

  setFormData((prev) => ({
    ...prev,
    diagnosis: [...prev.diagnosis, value],
    diagnosisInput: '',
  }));
};



  const handleNextStep = () => {
    if (currentStep === 1 && validatePatientInfo()) {
      setCurrentStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!validateConsent()) return;

    try {
      // Check if signature is empty
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
        toast.error('Please provide a signature to continue');
        return;
      }

      // Convert signature canvas to blob
      const dataUrl = signaturePadRef.current.toDataURL('image/png');
      const response = await fetch(dataUrl);
      const signatureBlob = await response.blob();

      // Create File from blob
      const signatureFile = new File(
        [signatureBlob],
        `${formData.fullName.replace(/\s+/g, '_')}_signature.png`,
        { type: 'image/png' }
      );

      // Create FormData payload
      const formDataPayload = new FormData();

      // Patient data
      formDataPayload.append('full_name', formData.fullName);
      formDataPayload.append('phone', formData.contactNumber);
      formDataPayload.append('age', formData.age);
      formDataPayload.append('gender', formData.gender);
      formDataPayload.append('address', formData.address || '');
      formDataPayload.append('diagnosis[]', formData.diagnosis);
      formDataPayload.append('treatment_plan', formData.treatmentPlan);
      formDataPayload.append('medical_history', formData.medicalHistory || '');

      // Consent data
      formDataPayload.append('consent_type_id', consentData.templateType);
      formDataPayload.append('consents[medical_photography]', consentData.medicalPhotographyConsent);
      formDataPayload.append('consents[research_academic_use]', consentData.researchAcademicConsent);
      formDataPayload.append('consents[treatment_documentation]', consentData.treatmentDocumentationConsent);

      // Signature image - append as file
      formDataPayload.append('signature_path', signatureFile, signatureFile.name);


      // API call with success/error handling
      addPatientMutation(formDataPayload, {
        onSuccess: (data) => {
          toast.success(`${formData.fullName} has been registered successfully`);
          navigate('/patients');
        },
        onError: (error) => {
          console.error('Patient creation error:', error);
          toast.error(error.response?.data?.message || 'Failed to create patient. Please try again.');
        }
      });

    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast.error('Failed to create patient. Please try again.');
    }
  };

  const consentSteps = consentTemplates.map((template, index) => ({
  id: template.id,
  name: template.name,
  description: template.description,
  key:
    index === 0
      ? 'medicalPhotographyConsent'
      : index === 1
      ? 'researchAcademicConsent'
      : 'treatmentDocumentationConsent',
}));



  // Find selected template by ID (templateType stores the ID as string)
  const selectedTemplate = consentTemplates.find(
    (t) => String(t.id) === consentData.templateType
  ) || null;

  const { mutate: addPatientMutation, isPending: isAddingPatient } = useAddPatient();

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}


      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        <div className={`flex items-center gap-2 ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white' : 'bg-gray-200'}`}>
            1
          </div>
          <span className="font-medium hidden sm:inline">Patient Info</span>
        </div>
        <div className="w-16 h-0.5 bg-gray-300"></div>
        <div className={`flex items-center gap-2 ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white' : 'bg-gray-200'}`}>
            2
          </div>
          <span className="font-medium hidden sm:inline">Consent Form</span>
        </div>
      </div>

      {/* Step 1: Patient Information */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary-600" />
              Patient Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="fullName"
                  placeholder="Enter full name"
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z\s]*$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }} 
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactNumber">Contact Number <span className="text-red-500">*</span></Label>
                <Input
                  id="contactNumber"
                  placeholder="9999999999"
                  maxlength={10}
                  onKeyPress={(e) => {
                    if (!/^\d*$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  value={formData.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="age">Age <span className="text-red-500">*</span></Label>
                <Input
                  id="age"
                  type="tel"
                  maxlength={2}
                  onKeyPress={(e) => {
                    if (!/^\d*$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter age"
                  value={formData.age}
                  onChange={(e) => handleInputChange('age', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender">Gender <span className="text-red-500">*</span></Label>
                <Select value={formData.gender} onValueChange={(value) => handleInputChange('gender', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className=' grid grid-cols-1 sm:grid-cols-2 gap-5 '>

              <div className="space-y-2 ">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="Enter address"
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}


                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="diagnosis">
                  Diagnosis <span className="text-red-500">*</span>
                </Label>

                <div className="flex flex-wrap items-center gap-2 border rounded-md p-2 min-h-[42px]">
                  {formData.diagnosis.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeDiagnosis(index)}
                        className="hover:text-red-600"
                      >
                        ✕
                      </button>
                    </span>
                  ))}

                 <input
  type="text"
  className="flex-1 outline-none min-w-[120px]"
  placeholder="Type diagnosis and press comma"
  value={formData.diagnosisInput}
  onChange={(e) =>
    setFormData((prev) => ({ ...prev, diagnosisInput: e.target.value }))
  }
  onKeyDown={handleDiagnosisKeyDown}
  onBlur={commitDiagnosisInput}  
/>

                </div>


              </div>


            </div>

            <div className=' grid grid-cols-1  sm:grid-cols-2 gap-5 '>
              <div className="space-y-2 ">
                <Label htmlFor="treatmentPlan">Treatment Plan <span className="text-red-500">*</span></Label>
                <Textarea
                  id="treatmentPlan"
                  placeholder="Enter treatment plan"
                  value={formData.treatmentPlan}
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => handleInputChange('treatmentPlan', e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-2 ">
                <Label htmlFor="medicalHistory">Medical History</Label>
                <Textarea
                  id="medicalHistory"
                  placeholder="Enter medical history"
                  value={formData.medicalHistory}
                  onKeyPress={(e) => {
                    if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => handleInputChange('medicalHistory', e.target.value)}
                  rows={4}

                />
              </div>
            </div>


            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate('/patients')}>
                Cancel
              </Button>
              <Button
                onClick={handleNextStep}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
              >
                Next: Consent Form
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Consent Form */}
     {currentStep === 2 && (
  <>
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600" />
          Consent Form
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6 ">
        {/* Progress Indicator */}
        <div className="flex items-center gap-2 justify-center">
          {consentSteps.map((_, index) => (
            <div
              key={index}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
              ${index <= consentStep ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'}`}
            >
              {index + 1}
            </div>
          ))}
        </div>

        {/* Active Consent Step */}
        {consentSteps[consentStep] && (
          <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-900">
              {consentSteps[consentStep].name}
            </h4>

            <p className="text-sm text-gray-700 min-h-[50px] max-h-[300px] overflow-y-auto whitespace-pre-line">
              {consentSteps[consentStep].description}
            </p>

            <RadioGroup
              value={consentData[consentSteps[consentStep].key]}
              onValueChange={(value) =>
                setConsentData((prev) => ({
                  ...prev,
                  [consentSteps[consentStep].key]: value,
                }))
              }
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="consent-yes" />
                <Label htmlFor="consent-yes">I Consent</Label>
              </div>

              <div className="flex items-center space-x-2">
                <RadioGroupItem value="0" id="consent-no" />
                <Label htmlFor="consent-no">I Don’t Consent</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            disabled={consentStep === 0}
            onClick={() => setConsentStep((s) => s - 1)}
          >
            Back
          </Button>

          {consentStep < consentSteps.length - 1 ? (
            <Button
              disabled={!consentData[consentSteps[consentStep]?.key]}
              onClick={() => setConsentStep((s) => s + 1)}
            >
              Next
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>

    {/* SIGNATURE — ONLY AFTER ALL CONSENTS */}
    
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-primary-600" />
            Digital Signature
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <SignatureCanvas
              ref={signaturePadRef}
              canvasProps={{ className: 'w-full h-72' }}
              backgroundColor="white"
            />
          </div>

          <Button variant="outline" onClick={clearSignature}>
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
        </CardContent>
      </Card>

  

    {/* FINAL ACTIONS */}
    <div className="flex justify-between mt-4">
      <Button variant="outline" onClick={() => setCurrentStep(1)}>
        Back
      </Button>

      <Button
        onClick={handleSubmit}
        disabled={isAddingPatient}
        className="bg-gradient-to-r from-primary-500 to-primary-600 text-white"
      >
        Submit & Create Patient
        {isAddingPatient && <Loader className="w-4 h-4 ml-2 animate-spin" />}
      </Button>
    </div>
  </>
)}


    </div>
  );
};

export default AddPatient;
